import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import Info from '@mui/icons-material/Info'
import Avatar from '@mui/material/Avatar'
import Badge from '@mui/material/Badge'
import Collapse from '@mui/material/Collapse'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemText from '@mui/material/ListItemText'
import moment from 'moment'
import { useState } from 'react'
import { makeStyles } from 'tss-react/mui'

import analytics from '@src/analytics'
import Icon from '@src/components/shared/Icon'

import { AlertOnClickContext, NormalizedAlert } from './normalizeAlert'

const useStyles = makeStyles()((theme) => ({
  iconWrapper: {
    'backgroundColor': '#808080',

    // Even though we let alerts have an icon, icon size, and icon background
    // color, since the whole thing is not just a simple .svg with proper
    // positioning, some fine adjustments may need to be made.
    // Since the alert container has a class of the alert category, we can use
    // that to tweak the design.
    '.facebook_page_invalid & svg, .no_facebook_pages_connected & svg, .failed_facebook_share & svg':
      {
        position: 'relative',
        left: -2,
      },
  },
  expandContainer: {
    alignSelf: 'center',
  },
  insetAvatar: {
    transform: 'scale(0.7)',
    transformOrigin: 'right top',
  },
  unreadNotification: {
    opacity: theme.palette.action.disabledOpacity,
  },
}))

interface NotificationProps {
  alert: NormalizedAlert
  markAsRead: (id: number) => Promise<void>
  inset?: boolean
  context: AlertOnClickContext
}

const Notification: React.FC<React.PropsWithChildren<NotificationProps>> = (
  props,
) => {
  const { classes, cx } = useStyles(undefined, {
    props,
  })
  const originalAlert = props.alert.alert

  const [isExpanded, setIsExpanded] = useState(false)

  const handleClick = async () => {
    if (hasChildren) {
      setIsExpanded(!isExpanded)
      return
    }

    analytics.track({
      event: 'Click',
      type: 'Alerts Mark as Read',
      data: {
        category: originalAlert.category,
        alertable_id: originalAlert.alertable_id,
        alertable_type: originalAlert.alertable_type,
        subjectable_type: originalAlert.subjectable_type,
        subjectable_id: originalAlert.subjectable_id,
        alertId: originalAlert.id,
        status: 'read',
        subjectable: { ...originalAlert.subjectable },
      },
    })

    await props.markAsRead(originalAlert.id!)
    props.alert.onClick?.(props.context)
  }

  const iconChild =
    props.alert.icon == null ? (
      <Info color="disabled" fontSize="large" />
    ) : typeof props.alert.icon === 'string' ? (
      <Icon
        icon={props.alert.icon}
        size={props.alert.iconSize ?? 18}
        color={props.alert.iconColor || 'white'}
      />
    ) : (
      props.alert.icon
    )

  const hasChildren = Boolean(
    props.alert.showChildren !== false && props.alert.children?.length,
  )

  return (
    <>
      <ListItem
        button
        disableRipple
        onClick={handleClick}
        divider
        alignItems="flex-start"
        selected={props.alert.alert.status === 'unread'}
      >
        <ListItemAvatar>
          <Badge badgeContent={props.alert.badge?.()} color="error">
            <Avatar
              className={cx(classes.iconWrapper, {
                [classes.insetAvatar]: props.inset,
                [classes.unreadNotification]:
                  props.inset && props.alert.alert.status === 'read',
              })}
              style={{
                backgroundColor: props.alert.iconBackgroundColor,
              }}
            >
              {/* <Icon icon={props.alert.icon} /> */}
              {iconChild}
            </Avatar>
          </Badge>
        </ListItemAvatar>
        <ListItemText
          primary={props.alert.message}
          primaryTypographyProps={{ variant: 'body2' }}
          secondary={moment(props.alert.alert.created_at).fromNow()}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
        {hasChildren ? (
          <div className={classes.expandContainer}>
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </div>
        ) : undefined}
      </ListItem>
      {hasChildren ? (
        <Collapse in={isExpanded}>
          <List dense disablePadding>
            {props.alert.children?.map((alert) => (
              <Notification
                key={alert.alert.id}
                {...props}
                alert={alert}
                inset
              />
            ))}
          </List>
        </Collapse>
      ) : undefined}
    </>
  )
}

export default Notification
